import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const STORAGE_KEY = "sni_consent_v1";
const CONSENT_VERSION = 2;
const PH_TOKEN = "phc_DbsPb39SRc8z3EiQ6Dhj6ikv4H4rTKcht9d4sZSesceP";
const PH_HOST = "https://ap.emergent.sh";
const ADSENSE_CLIENT = "ca-pub-4746576617116760";
const ConsentContext = createContext(null);

const DEFAULT = { necessary: true, analytics: false, youtube: false, marketing: false };

// PostHog viene inizializzato SOLO al consenso alle statistiche.
// La registrazione delle sessioni resta sempre disattivata.
function applyToPostHog(consent) {
  try {
    const ph = window.posthog;
    if (!ph) return;
    if (consent.analytics) {
      if (!window.__sni_ph_inited && typeof ph.init === "function") {
        ph.init(PH_TOKEN, {
          api_host: PH_HOST,
          person_profiles: "identified_only",
          capture_pageview: true,
          autocapture: true,
          disable_session_recording: true,
          disable_surveys: true,
        });
        window.__sni_ph_inited = true;
      } else if (typeof ph.opt_in_capturing === "function") {
        ph.opt_in_capturing();
      }
    } else if (window.__sni_ph_inited && typeof ph.opt_out_capturing === "function") {
      ph.opt_out_capturing();
    }
  } catch (e) {
    console.warn("Consenso: applicazione delle preferenze PostHog non riuscita", e);
  }
}

// Google AdSense viene caricato SOLO dopo il consenso "marketing".
function applyAdSense(consent) {
  try {
    if (!consent.marketing || window.__sni_adsense_loaded) return;
    const s = document.createElement("script");
    s.async = true;
    s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
    s.crossOrigin = "anonymous";
    document.head.appendChild(s);
    window.__sni_adsense_loaded = true;
  } catch (e) {
    console.warn("Consenso: caricamento AdSense non riuscito", e);
  }
}

export function ConsentProvider({ children }) {
  const [consent, setConsentState] = useState(null); // null = non ancora deciso
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setConsentState(parsed);
        applyToPostHog(parsed);
        applyAdSense(parsed);
      }
    } catch (e) {
      console.warn("Consenso: lettura da localStorage non riuscita", e);
    }
  }, []);

  const persist = useCallback((next) => {
    const record = { ...DEFAULT, ...next, necessary: true, ts: new Date().toISOString(), version: CONSENT_VERSION };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch (e) {
      console.warn("Consenso: salvataggio su localStorage non riuscito", e);
    }
    setConsentState(record);
    applyToPostHog(record);
    applyAdSense(record);
    // Se l'utente revoca il marketing dopo che AdSense era già caricato, ricarico per rimuoverlo.
    if (window.__sni_adsense_loaded && !record.marketing) {
      window.location.reload();
    }
    return record;
  }, []);

  const acceptAll = useCallback(() => {
    persist({ analytics: true, youtube: true, marketing: true });
    setPrefsOpen(false);
  }, [persist]);

  const rejectNonEssential = useCallback(() => {
    persist({ analytics: false, youtube: false, marketing: false });
    setPrefsOpen(false);
  }, [persist]);

  const savePreferences = useCallback((choices) => {
    persist(choices);
    setPrefsOpen(false);
  }, [persist]);

  const grant = useCallback((category) => {
    persist({ ...(consent || DEFAULT), [category]: true });
  }, [consent, persist]);

  const openPreferences = useCallback(() => setPrefsOpen(true), []);
  const closePreferences = useCallback(() => setPrefsOpen(false), []);
  const has = useCallback((category) => Boolean(consent && consent[category]), [consent]);

  const value = useMemo(() => ({
    consent,
    decided: consent !== null,
    prefsOpen,
    openPreferences,
    closePreferences,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    grant,
    has,
  }), [consent, prefsOpen, openPreferences, closePreferences, acceptAll, rejectNonEssential, savePreferences, grant, has]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
  return ctx;
}
